/**
 * The platform panel's rules, as pure functions the screens and the tests share.
 *
 * The lifecycle moves mirror `platform_admin/operations.py`. The server is the authority:
 * these only decide which buttons are offered, so a stale client cannot make a move the
 * server would refuse, and the refusal text still comes from the server.
 */
import { ApiError } from "@/lib/api";

import type { DemoStatus, TenantStatus } from "@/api/platform";

export const TENANT_STATUS_LABEL: Readonly<Record<TenantStatus, string>> = {
  active: "Faol",
  suspended_billing: "To'lov bo'yicha to'xtatilgan",
  suspended_admin: "Administrator to'xtatgan",
  closed: "Yopilgan",
};

/** Which status a platform admin may move an agency to from each state. */
const MANUAL_MOVES: Readonly<Record<TenantStatus, readonly TenantStatus[]>> = {
  active: ["suspended_admin", "closed"],
  suspended_billing: ["active", "suspended_admin", "closed"],
  suspended_admin: ["active", "closed"],
  closed: [],
};

export function allowedTenantMoves(from: TenantStatus): readonly TenantStatus[] {
  return MANUAL_MOVES[from];
}

export function canMoveTenant(from: TenantStatus, to: TenantStatus): boolean {
  return MANUAL_MOVES[from].includes(to);
}

/** A reason is mandatory for every lifecycle change; whitespace alone does not count. */
export function reasonIsPresent(reason: string): boolean {
  return reason.trim().length > 0;
}

export const DEMO_STATUS_LABEL: Readonly<Record<DemoStatus, string>> = {
  new: "Yangi",
  contacted: "Bog'lanildi",
  demo_scheduled: "Demo belgilandi",
  converted: "Agentlikka aylantirildi",
  rejected: "Rad etildi",
};

/** Targets a sales person may choose by hand. `converted` is reached only by converting. */
export const DEMO_MANUAL_TARGETS: readonly DemoStatus[] = [
  "new",
  "contacted",
  "demo_scheduled",
  "rejected",
];

export function canChangeDemo(current: DemoStatus, to: DemoStatus): boolean {
  if (current === "converted" || to === "converted" || to === current) {
    return false;
  }
  return DEMO_MANUAL_TARGETS.includes(to);
}

export function canConvertDemo(current: DemoStatus, hasTenant: boolean): boolean {
  return current !== "converted" && current !== "rejected" && !hasTenant;
}

export { formatUzs } from "@/lib/format";

/** Splits the branch field on new lines and commas, trims, and drops blanks and repeats. */
export function parseBranches(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[\n,]/)) {
    const name = part.trim();
    if (name !== "" && !seen.has(name.toLowerCase())) {
      seen.add(name.toLowerCase());
      out.push(name);
    }
  }
  return out;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isEmail(value: string): boolean {
  return EMAIL.test(value.trim());
}

/** The server's own sentence when it has one; a fixed fallback otherwise. */
export function refusalText(error: unknown, fallback: string): string {
  if (error instanceof ApiError && typeof error.payload === "object" && error.payload !== null) {
    const detail = (error.payload as Record<string, unknown>).detail;
    if (typeof detail === "string" && detail !== "") {
      return detail;
    }
  }
  return fallback;
}

export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/** A supplier account's last check, in Uzbek. The codes are the backend's `ProviderHealthStatus`. */
export const PROVIDER_STATUS_LABEL: Readonly<Record<string, string>> = {
  ok: "Ishlayapti",
  auth_failed: "Kirish rad etildi",
  unavailable: "Javob bermadi",
  protocol_error: "Javob tushunarsiz",
};

/** The label for a check, or "Tekshirilmagan" when no check has run yet. */
export function providerStatusLabel(code: string): string {
  return code === "" ? "Tekshirilmagan" : (PROVIDER_STATUS_LABEL[code] ?? code);
}

/**
 * How long a supplier has been failing, as a badge tone. The canary alerts the owner at the
 * third failure in a row (D169), so that is where the badge turns red.
 */
export const STREAK_ALERT_AT = 3;

export function streakTone(failures: number): "danger" | "warning" | "neutral" {
  if (failures >= STREAK_ALERT_AT) return "danger";
  if (failures > 0) return "warning";
  return "neutral";
}

/** The journal's severity, as the badge tone it is shown with. */
export function severityTone(severity: string): "danger" | "warning" | "neutral" {
  if (severity === "critical") return "danger";
  if (severity === "warning") return "warning";
  return "neutral";
}

/** Languages the create form offers. Only one is shipped (D272); the server refuses the rest. */
export const CREATE_LANGUAGES: readonly string[] = ["uz-latn"];

/** The two currencies an agency can report in (the closed list the server enforces). */
export const CREATE_CURRENCIES: readonly ("UZS" | "USD")[] = ["UZS", "USD"];

/**
 * Whether the panel shows the password link to copy rather than saying the owner has it by
 * e-mail (D580). The link is returned in both cases, so the panel never has to ask again.
 */
export function showsLinkToCopy(created: { invitation_sent: boolean }): boolean {
  return !created.invitation_sent;
}

/** The list's last-activity cell: the date of the newest sign-in, or "Kirmagan" for none. */
export function lastActivityText(lastActivityAt: string | null): string {
  return lastActivityAt === null ? "Kirmagan" : lastActivityAt.slice(0, 10);
}

/** The list's package cell: the package's name, or "Paketsiz" when the agency has none. */
export function tariffText(tariff: string | null): string {
  return tariff ?? "Paketsiz";
}

/** A count of channels that cannot carry messages reads as danger only when it is not zero. */
export function channelAttentionTone(count: number): "danger" | "neutral" {
  return count > 0 ? "danger" : "neutral";
}
